import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Mail, 
  MailOpen,
  Trash2,
  CheckCheck,
  Loader2,
  Inbox as InboxIcon,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Gift,
  MessageCircle,
  Bell,
  ExternalLink
} from 'lucide-react';

export default function Inbox() {
  const { user, refreshUnreadCount } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Get icon and color based on notification type
  const getNotificationStyle = (message) => {
    const type = message.notification_type;
    switch (type) {
      case 'confession_approved':
        return {
          icon: CheckCircle2,
          bgClass: 'bg-green-500/20',
          iconClass: 'text-green-400',
          badge: 'Aprovada',
          badgeClass: 'bg-green-500/20 text-green-400 border-green-500/30'
        };
      case 'confession_rejected':
        return {
          icon: XCircle,
          bgClass: 'bg-red-500/20',
          iconClass: 'text-red-400',
          badge: 'Rejeitada',
          badgeClass: 'bg-red-500/20 text-red-400 border-red-500/30'
        };
      case 'reward_redeemed':
        return {
          icon: Gift,
          bgClass: 'bg-gold/20',
          iconClass: 'text-gold',
          badge: 'Recompensa',
          badgeClass: 'bg-gold/20 text-gold border-gold/30'
        };
      default:
        if (message.is_broadcast) {
          return {
            icon: Bell,
            bgClass: !message.is_read ? 'bg-ruby/20' : 'bg-white/5',
            iconClass: !message.is_read ? 'text-ruby' : 'text-muted-foreground',
            badge: 'Comunicado',
            badgeClass: 'border-ruby/50 text-ruby'
          };
        }
        return {
          icon: message.is_read ? MailOpen : Mail,
          bgClass: !message.is_read ? 'bg-ruby/20' : 'bg-white/5',
          iconClass: !message.is_read ? 'text-ruby' : 'text-muted-foreground',
          badge: null,
          badgeClass: ''
        };
    }
  };

  // Get action button based on notification type
  const getNotificationAction = (message) => {
    const type = message.notification_type;
    const data = message.notification_data || {};
    
    switch (type) {
      case 'confession_approved':
        return data.confession_id ? {
          label: 'Ver Confissão',
          onClick: () => navigate(`/confessions/${data.confession_id}`)
        } : null;
      case 'reward_redeemed':
        return {
          label: 'Ver Os Meus Códigos',
          onClick: () => navigate('/points')
        };
      default:
        return null;
    }
  };

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      const response = await api.get('/messages');
      setMessages(response.data);
    } catch (error) {
      toast.error('Erro ao carregar mensagens');
    } finally {
      setLoading(false);
    }
  };

  const openMessage = async (message) => {
    setSelectedMessage(message);
    setDialogOpen(true);

    // Mark as read if not already
    if (!message.is_read) {
      try {
        await api.put(`/messages/${message.id}/read`);
        setMessages(prev => 
          prev.map(m => 
            m.id === message.id ? { ...m, is_read: true } : m
          )
        );
        // Refresh the unread count in navbar
        refreshUnreadCount();
      } catch (error) {
        console.error('Error marking as read:', error);
      }
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put('/messages/read-all');
      setMessages(prev => prev.map(m => ({ ...m, is_read: true })));
      toast.success('Todas mensagens marcadas como lidas');
      // Refresh the unread count in navbar
      refreshUnreadCount();
    } catch (error) {
      toast.error('Erro ao marcar mensagens');
    }
  };

  const deleteMessage = async (messageId, e) => {
    e?.stopPropagation();
    try {
      await api.delete(`/messages/${messageId}`);
      const wasUnread = messages.find(m => m.id === messageId && !m.is_read);
      setMessages(prev => prev.filter(m => m.id !== messageId));
      if (selectedMessage?.id === messageId) {
        setDialogOpen(false);
        setSelectedMessage(null);
      }
      toast.success('Mensagem eliminada');
      // Refresh the unread count if deleted message was unread
      if (wasUnread) {
        refreshUnreadCount();
      }
    } catch (error) {
      toast.error('Erro ao eliminar mensagem');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = diffMs / (1000 * 60 * 60);
    const diffDays = diffMs / (1000 * 60 * 60 * 24);

    if (diffHours < 1) {
      const mins = Math.floor(diffMs / (1000 * 60));
      return `${mins} min atrás`;
    } else if (diffHours < 24) {
      return `${Math.floor(diffHours)}h atrás`;
    } else if (diffDays < 7) {
      return `${Math.floor(diffDays)} dias atrás`;
    } else {
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit'
      });
    }
  };

  const unreadCount = messages.filter(m => !m.is_read).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-ruby" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 pt-24 pb-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-3" data-testid="inbox-title">
              <InboxIcon className="w-8 h-8 text-ruby" />
              Caixa de Entrada
            </h1>
            <p className="text-muted-foreground mt-1">
              {messages.length} mensagens • {unreadCount} não lidas
            </p>
          </div>
          
          {unreadCount > 0 && (
            <Button
              variant="outline"
              onClick={markAllAsRead}
              className="border-border"
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="w-4 h-4 mr-2" />
              Marcar Todas como Lidas
            </Button>
          )}
        </div>

        {/* Messages List */}
        {messages.length === 0 ? (
          <Card className="bg-surface border-border">
            <CardContent className="py-16 text-center">
              <InboxIcon className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-medium text-foreground mb-2">A sua caixa de entrada está vazia</h3>
              <p className="text-muted-foreground">
                Receberá notificações e mensagens da plataforma aqui
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {messages.map((message) => {
              const style = getNotificationStyle(message);
              const NotificationIcon = style.icon;
              
              return (
                <Card
                  key={message.id}
                  className={`bg-surface border-border cursor-pointer transition-all hover:bg-surface-light ${
                    !message.is_read ? 'border-l-4 border-l-ruby' : ''
                  }`}
                  onClick={() => openMessage(message)}
                  data-testid={`message-item-${message.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div className={`p-2 rounded-full ${style.bgClass}`}>
                        <NotificationIcon className={`w-5 h-5 ${style.iconClass}`} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className={`font-medium truncate ${!message.is_read ? 'text-white' : 'text-muted-foreground'}`}>
                            {message.subject}
                          </h3>
                          {style.badge && (
                            <Badge variant="outline" className={`text-xs shrink-0 ${style.badgeClass}`}>
                              {style.badge}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground truncate">
                          {message.content}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-muted-foreground">
                            De: {message.sender_name}
                          </span>
                          <span className="text-xs text-muted-foreground">•</span>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(message.created_at)}
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-red-400 shrink-0"
                        onClick={(e) => deleteMessage(message.id, e)}
                        data-testid={`delete-message-${message.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Message Detail Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-surface border-border max-w-2xl z-50">
            <DialogHeader>
              <DialogTitle className="text-foreground flex items-center gap-2">
                {selectedMessage && (() => {
                  const style = getNotificationStyle(selectedMessage);
                  const NotificationIcon = style.icon;
                  return <NotificationIcon className={`w-5 h-5 ${style.iconClass}`} />;
                })()}
                {selectedMessage?.subject}
              </DialogTitle>
            </DialogHeader>
            
            {selectedMessage && (
              <div className="space-y-4">
                <div className="flex items-center justify-between text-sm text-muted-foreground border-b border-border pb-3">
                  <div className="flex items-center gap-2">
                    <span>De: <span className="text-foreground">{selectedMessage.sender_name}</span></span>
                    {(() => {
                      const style = getNotificationStyle(selectedMessage);
                      return style.badge ? (
                        <Badge variant="outline" className={`text-xs ${style.badgeClass}`}>
                          {style.badge}
                        </Badge>
                      ) : null;
                    })()}
                  </div>
                  <span>{formatDate(selectedMessage.created_at)}</span>
                </div>

                <div 
                  className="prose prose-invert max-w-none"
                  data-testid="message-content-view"
                >
                  <p className="text-muted-foreground whitespace-pre-wrap">
                    {selectedMessage.content}
                  </p>
                </div>

                {/* Action button for notifications */}
                {(() => {
                  const action = getNotificationAction(selectedMessage);
                  return action ? (
                    <div className="pt-2">
                      <Button
                        onClick={() => {
                          setDialogOpen(false);
                          action.onClick();
                        }}
                        className="w-full bg-ruby hover:bg-ruby-light"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        {action.label}
                      </Button>
                    </div>
                  ) : null;
                })()}

                <div className="flex justify-between pt-4 border-t border-border">
                  <Button
                    variant="ghost"
                    onClick={() => setDialogOpen(false)}
                    className="text-muted-foreground"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Voltar
                  </Button>
                  <Button
                    variant="ghost"
                    className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                    onClick={() => deleteMessage(selectedMessage.id)}
                    data-testid="delete-open-message-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar Mensagem
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
